import type { FlagDefinition, PatientFlagEmbed } from '@/types/patient-flag';

/** Normalize PUT assign/unassign response bodies into a flag list. */
export const parsePatientFlagsPayload = (data: unknown): PatientFlagEmbed[] => {
    if (Array.isArray(data)) {
        return data as PatientFlagEmbed[];
    }
    if (data && typeof data === 'object') {
        const record = data as Record<string, unknown>;
        if (Array.isArray(record.flags)) {
            return record.flags as PatientFlagEmbed[];
        }
        if (Array.isArray(record.items)) {
            return record.items as PatientFlagEmbed[];
        }
        if (typeof record.id === 'string' && typeof record.name === 'string' && typeof record.color === 'string') {
            return [data as PatientFlagEmbed];
        }
    }
    return [];
};

const isActiveEmbedList = (flags: PatientFlagEmbed[]): boolean => flags.every((flag) => typeof flag.isOverride === 'boolean');

const isFullDefinitionList = (flags: PatientFlagEmbed[], definitions: FlagDefinition[]): boolean => {
    if (!definitions.length || flags.length !== definitions.length) {
        return false;
    }
    const responseIds = new Set(flags.map((flag) => flag.id));
    return definitions.every((definition) => responseIds.has(definition.id));
};

type ResolveNextPatientFlagsArgs = {
    data: unknown;
    flagId: string;
    assigned: boolean;
    previousFlags: PatientFlagEmbed[];
    definitions: FlagDefinition[];
};

/**
 * Resolve the patient's next active flags after assign/unassign.
 * Prefers a trusted API active-flag list; falls back to toggling one flag locally
 * so a definitions-shaped response cannot mark every checkbox selected.
 */
export const resolveNextPatientFlags = ({
    data,
    flagId,
    assigned,
    previousFlags,
    definitions
}: ResolveNextPatientFlagsArgs): PatientFlagEmbed[] => {
    const parsed = parsePatientFlagsPayload(data);
    const trustedActiveList =
        isActiveEmbedList(parsed) &&
        (Array.isArray(data) ||
            (Boolean(data) &&
                typeof data === 'object' &&
                (Array.isArray((data as { flags?: unknown }).flags) || Array.isArray((data as { items?: unknown }).items)))) &&
        !isFullDefinitionList(parsed, definitions);

    if (trustedActiveList) {
        const hasFlag = parsed.some((flag) => flag.id === flagId);
        if (assigned ? hasFlag : !hasFlag) {
            return parsed;
        }
    }

    if (assigned) {
        const definition = definitions.find((flag) => flag.id === flagId);
        const fromResponse =
            parsed.find((flag) => flag.id === flagId) ?? (parsed.length === 1 && parsed[0]?.id === flagId ? parsed[0] : undefined);

        const embed: PatientFlagEmbed | null = fromResponse
            ? { ...fromResponse, id: flagId }
            : definition
              ? {
                    ...definition,
                    isOverride: definition.systemKey === 'dc_today'
                }
              : null;

        if (!embed) {
            return previousFlags;
        }

        return [...previousFlags.filter((flag) => flag.id !== flagId), embed];
    }

    return previousFlags.filter((flag) => flag.id !== flagId);
};
